import { Directive, Input, AfterContentInit, QueryList, ContentChildren, EventEmitter, Output, HostBinding, OnDestroy } from '@angular/core';
import { noop, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { RadioButtonComponent, RadioEventArgs } from '../radio-button/radio-button.component';

@Directive({
    selector: 'mpro-radio-group, [mproRadioGroup]',
})
export class RadioGroupDirective implements OnDestroy, AfterContentInit {

    @ContentChildren(RadioButtonComponent, { descendants: true }) public radioButtons: QueryList<RadioButtonComponent>;

    @Output() public readonly change: EventEmitter<RadioEventArgs> = new EventEmitter<RadioEventArgs>();

    @Input()
    public get value(): any {
        return this._value;
    }
    public set value(newValue: any) {
        if (this._value !== newValue) {
            this._value = newValue;
            this._selectRadioButton();
        }
    }

    @HostBinding("class.disabled")
    @Input()
    public get disabled(): boolean {
        return this._disabled;
    }
    public set disabled(value: boolean) {
        this._disabled = (value as any === '') || value;
        this.setDisabled(value);
    }

    @Input()
    public get selected() {
        return this._selected;
    }
    public set selected(selected: RadioButtonComponent | null) {
        if (this._selected !== selected) {
            this._selected = selected;
            this.value = selected ? selected.value : null;
        }
    }

    @Input()
    public get alignment(): string {
        return this.vertical ? 'vertical' : 'horizontal';
    }

    public set alignment(value: string) {
        this.vertical = value === 'vertical';
    }

    @HostBinding('class.vertical')
    private vertical = false;

    @Input() dark: boolean = false;

    private _onChangeCallback: (_: any) => void = noop;

    private _value: any = null

    private _disabled: boolean = false

    private _selected: RadioButtonComponent | null = null;

    private ngUnsubscribe: Subject<void> = new Subject();

    public _isInitialized = false;

    ngAfterContentInit() {
        this._isInitialized = true;
        setTimeout(() => {
            this._initRadioButtons();
        });
    }

    public ngOnDestroy(): void {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }

    private _initRadioButtons() {
        if (this.radioButtons) {
            const props = { disabled: this._disabled, dark: this.dark };
            this.radioButtons.forEach((button) => {
                Object.assign(button, props);
                if (button.value === this._value) {
                    button.checked = true;
                    this._selected = button;
                }

                button.change.pipe(takeUntil(this.ngUnsubscribe)).subscribe((ev) => this._selectedRadioButtonChanged(ev));
            });
        }
    }

    private _selectedRadioButtonChanged(args: RadioEventArgs) {
        this.radioButtons.forEach(button => {
            button.checked = button.value === args.value;
        });

        this._selected = args.radio;
        this._value = args.value;

        if (this._isInitialized) {
            this.change.emit(args);
            this._onChangeCallback(this.value);
        }
    }

    private _selectRadioButton() {
        if (this.radioButtons) {
            this.radioButtons.forEach((button) => {
                if (!this._value) {
                    if (button.checked) {
                        button.checked = false;
                    }
                } else {
                    if (this._value === button.value) {
                        if (this._selected !== button) {
                            this._selected = button;
                        }

                        if (!button.checked) {
                            button.select();
                        }
                    } else {
                        if (button.checked) {
                            button.checked = false;
                        }
                    }
                }
            });
        }
    }

    public setDisabled(isDisabled: boolean) {
        if (this.radioButtons) {
            this.radioButtons.forEach((button) => {
                button.disabled = isDisabled;
            });
        }
    }

    public registerOnChange(fn: (_: any) => void) {
        this._onChangeCallback = fn;
    }

}
